import { groupBy, indexItemsById } from '@/helpers/IndexHelpers'
import { sum } from '@/helpers/MiscellaneousHelpers'
import type { CompletedOrdersResponse } from '@/checkout/helpers/completing'

// Create a collection of 'products' for Google Analytics. Each product is based on the
// ticket type of each ticket in the order, and by extension, the parent ticket group and
// event template of each of those ticket types. Multiple tickets of the same ticket type
// results in a greater quantity property of the product.
interface Product {
  id: string
  name: string
  variant: string
  brand: string
  category: string
  // TODO Use separate properties for item prices and total.
  price: any // string | number[]
  coupon: string | null
  quantity: number
}

function getProducts(
  tickets: Ticket[],
  eventTemplates: Dict<EventTemplate>,
  cartmods: CartMod[],
  ticketTypes: Dict<TicketType>,
  ticketGroups: Dict<TicketGroup>,
): Product[] {
  return []
}

/**
 * TODO Needs test coverage.
 * TODO Split into smaller simpler functions.
 * @deprecated Use `getPurchasePayloads()`
 */
export function pushPurchaseEventToGoogleTagManager(
  checkoutResponse: CompletedOrdersResponse,
  templates: EventTemplate[],
  types: TicketType[],
  cartmods: CartMod[],
) {
  // Reuse the event data available from cart. None of this is included in the checkout response.
  const eventTemplates = indexItemsById(templates)
  const ticketTypes = indexItemsById(types)
  const ticketGroups = indexItemsById(checkoutResponse.ticket_group._data)

  // Group all tickets by ticket order.
  const ticketsByOrder = groupBy('ticket_order_id', checkoutResponse.ticket._data)

  // Based on original portal string GTM integration for The Broad.
  for (const ticketOrder of checkoutResponse.ticket_order._data) {
    const tickets = ticketsByOrder[ticketOrder.id]

    // TODO Move most of this loop to getProducts().
    // const products = getProducts(tickets, eventTemplates, cartmods, ticketTypes, ticketGroups)

    // Create a collection of 'products' for Google Analytics. Each product is based on the
    // ticket type of each ticket in the order, and by extension, the parent ticket group and
    // event template of each of those ticket types. Multiple tickets of the same ticket type
    // results in a greater quantity property of the product.

    const productsMap: Dict<Product> = {}

    for (const ticket of tickets) {
      if (ticket.state !== 'booked') {
        continue
      }

      let product = productsMap[ticket.ticket_type_id]
      if (!product) {
        const eventTemplate = eventTemplates[ticket.event_template_id]

        // For 'Product Coupon', search cartmods which have affected this ticket. Only use the
        // first. Also use the cartmod name instead of the code which may activated it - it is
        // not easy to map a cartmod back to the code that activated it (if a code did, at all.)
        // This is still imperfect as only the first ticket of this ticket type in the order is
        // going to trigger the cartmod search.
        const cartmod = cartmods.find(({ tickets_affected }) => tickets_affected!.includes(ticket.id))

        product = {
          id: ticket.event_template_id,
          name: eventTemplate.name,
          variant: ticketTypes[ticket.ticket_type_id].name,
          brand: ticketGroups[ticket.ticket_group_id].name,
          category: eventTemplate.category,
          price: [],
          coupon: cartmod ? cartmod.name : null,
          quantity: 0,
        }
        productsMap[ticket.ticket_type_id] = product
      }

      // Collect the values of each purchased product to be used for an average
      product.price.push(parseFloat(ticket.adjusted_value))
      product.quantity++
    }

    // Use the values of the product map as the products array for GA
    const products = Object.values(productsMap)

    // Calculate average prices
    products.forEach((tt) => {
      tt.price = (sum(tt.price as number[]) / tt.quantity).toFixed(2)
    })

    // TODO sumTicketPrices is available but it uses face_value instead
    const revenue = sum(tickets.map(({ adjusted_value }) => parseFloat(adjusted_value))).toFixed(2)

    // The original of this was in <CompletedOrdersRoute>.
    // Event name was 'conversion' - but 'purchase' is used in the docs
    // It included send_to - if we don't include send_to, is the event sent to all GTM configurations?
    // send_to: this.$portalString.gtag_send_to,

    // window.dataLayer.push({
    //   event: 'purchase',
    //   // gtm params: transaction_id, value, currency, tax, shipping, items, coupon
    //   value: revenue,
    //   currency: this.$portal.default_currency_code,
    //   transaction_id: ticketOrder.order_number,
    // })

    // A note on the event name:
    // This payload is like what The Broad uses, but The Broad excludes the event name.
    // GTM in-browser preview says 'No tags were evaluated because there was no Event in the
    // selected Message'
    // Presumably is still works with the native GA integration for GTM, but we would probably
    // need to include an event name for tenants to trigger their own GTM tags.
    // I have included it here for that purpose.
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        purchase: {
          actionField: {
            id: ticketOrder.order_number,
            affiliation: null,
            revenue, // Should be string - ie. toFixed() above
            tax: null,
            shipping: null,
            // Use the first cartmod in the whole order for the 'Order Coupon' instead of the 'Product Coupon'
            coupon: cartmods.length ? cartmods[0].name : null,
          },
          products,
        },
      },
    })
  }
}
